import { useRef, useState } from "react";
import { Badge, badgeVariants } from "./badge";
import { cn } from "@/lib/utils";
import { VariantProps } from "class-variance-authority";

type BadgeInputProps = {
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  onChange: (value: string[]) => void;
  withBackground?: boolean;
  withBorder?: boolean;
  value: string[];
  variant?: VariantProps<typeof badgeVariants>["variant"];
} & Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  "onChange"
>;

export default function BadgeInput({
  withBackground = false,
  withBorder = true,
  variant = "purple",
  inputProps,
  onChange,
  value,
  ...rest
}: BadgeInputProps) {
  // Local state
  const [inputValue, setInputValue] = useState<string>("");
  const [nextInsertIndex, setNextInsertIndex] = useState<number>();
  const [selectedWords, setSelectedWords] = useState<string[]>([]);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);

  // Helpers
  function filterAndTrimWords(wordsString: string) {
    const words = wordsString.split(",");
    const trimmedWords = words.map((word) => word.trim());
    const uniqueWords = trimmedWords.filter((word) => {
      const isDuplicateWord = value.includes(word);
      return Boolean(word) && !isDuplicateWord;
    });

    return [...new Set(uniqueWords)];
  }

  // Handlers
  function handleChangeWords(wordsString: string) {
    const words = filterAndTrimWords(wordsString);

    const newValue = value.toSpliced(
      typeof nextInsertIndex === "number" ? nextInsertIndex : value.length,
      0,
      ...words
    );

    onChange(newValue);

    setNextInsertIndex(undefined);

    setTimeout(() => {
      setInputValue("");
    }, 0);
  }

  function handleDeleteWord(index: number) {
    const newValue = value.toSpliced(index, 1);
    onChange(newValue);
  }

  function handleDeleteWords(words: string[]) {
    const newValue = value.filter((word) => !words.includes(word));
    onChange(newValue);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Add word
    if ((e.key === "Enter" || e.key === "Tab") && e.currentTarget.value) {
      e.preventDefault();
      handleChangeWords(e.currentTarget.value);
    }
    // Remove word
    else if (e.key === "Backspace" && !e.currentTarget.value && value.length) {
      if (selectedWords.length > 0) {
        handleDeleteWords(selectedWords);
        setSelectedWords([]);
      } else {
        handleDeleteWord(value.length - 1);
      }
    }
    // Select all words
    else if (e.key === "a" && (e.ctrlKey || e.metaKey)) {
      setSelectedWords(value);
    }
    // Copy words
    else if (e.key === "c" && (e.ctrlKey || e.metaKey)) {
      navigator.clipboard.writeText(selectedWords.join(", "));
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasteValue = e.clipboardData.getData("text");
    handleChangeWords(pasteValue);
  }

  function handleEditWord(word: string, index: number) {
    handleDeleteWord(index);
    setInputValue(word);

    if (inputRef.current) {
      setNextInsertIndex(index);
      inputRef.current.focus();
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setSelectedWords([]);

    if (e.currentTarget.value) {
      handleChangeWords(e.currentTarget.value);
    }
  }

  return (
    <div
      {...rest}
      role="group"
      className={cn(
        "badge-input cursor-text w-full flex items-end gap-x-1 gap-y-1.5 flex-wrap px-2 py-1.75 group/input-group relative rounded-md transition-[color,box-shadow] outline-none",

        withBorder &&
          cn(
            "border border-input shadow-xs",
            // Focus state.
            "has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]",

            // Error state.
            "has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40"
          ),

        withBackground && "bg-white dark:bg-input/30"
      )}
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      {value.map((word, i) => {
        const isSelected = selectedWords.includes(word);

        return (
          <Badge
            key={`${word}-${i}`}
            variant={isSelected ? "default" : variant}
            onDelete={() => {
              handleDeleteWord(i);
            }}
            shape="square"
            className="h-5 text-sm"
            onDoubleClick={() => {
              handleEditWord(word, i);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              inputRef.current?.focus();

              if (isSelected) {
                setSelectedWords((prev) => prev.filter((w) => w !== word));
              } else if (e.metaKey || e.ctrlKey) {
                setSelectedWords((prev) => [...prev, word]);
              } else {
                setSelectedWords([word]);
              }
            }}
          >
            {word}
          </Badge>
        );
      })}

      <input
        {...inputProps}
        ref={inputRef}
        data-slot="input-group-control"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.currentTarget.value);
        }}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={cn(
          "min-w-20 flex-1 focus:outline-none md:text-sm px-1",
          inputProps?.className
        )}
      />
    </div>
  );
}
